/**
 * SelfAttestation — the v2 ceremony's identity self-attestation step (CB-5.2, FR-77).
 *
 * A lightweight identity MITIGATION, not strong proofing: the signer affirmatively confirms
 * "I am {name}, authorized to sign" before proceeding. On confirm it calls the token-scoped public
 * endpoint, which stamps `contract_signers.self_attested_at` and records one append-only
 * `self_attested` audit event — adding an attributable, timestamped moment to the legal record.
 *
 * UX-DR20 (legal moments are explicit, never sneaky): the affirmation is a first-class, NOT
 * pre-checked control; Continue stays disabled until the signer actively confirms; plain framing,
 * no dark-pattern nudging, and microcopy that does not overclaim (we do not verify identity).
 *
 * Sits inside the CB-5.1 SignContractPageV2 shell. The signer's name is passed in from the
 * token-resolved row — never typed by the signer.
 */

import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { contractService } from '@/services/contractService';

interface SelfAttestationProps {
  /** The signer's resolved name (from the token-scoped loader). May be null. */
  signerName: string | null;
  /** Per-signer access_token (the sole credential for the public action). */
  token: string;
  /** Tenant accent + the contrast-guarded label ink, computed by the shell. */
  accent: string;
  ink: string;
  /** Called once the attestation is recorded (or already present) so the shell can advance. */
  onAttested: () => void;
}

export default function SelfAttestation({ signerName, token, accent, ink, onAttested }: SelfAttestationProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayName = (signerName || '').trim();
  // Exact affirmative label per FR-77, with a natural fallback when no name is on file.
  const label = displayName
    ? `I am ${displayName}, authorized to sign.`
    : 'I am authorized to sign this contract.';

  async function handleContinue() {
    if (!confirmed || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await contractService.selfAttestV2(token);
    if (result.success) {
      onAttested();
      return;
    }
    // Recoverable inline message — never a raw error (UX-DR20 / EXPERIENCE.md voice).
    setError(result.error || 'We couldn\'t record your confirmation. Please try again.');
    setSubmitting(false);
  }

  return (
    <section className="mt-6" aria-labelledby="attest-heading">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 id="attest-heading" className="text-lg font-semibold text-slate-900">
          Confirm your identity
        </h2>
        <p className="mt-1 text-base leading-relaxed text-slate-600">
          Before you sign, please confirm this is you. This is a self-attestation for the contract's
          record — it isn't an identity check.
        </p>

        {/* Affirmative control — NOT pre-checked, visually first-class (UX-DR20). */}
        <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 focus-within:ring-2 focus-within:ring-offset-2">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
            style={{ accentColor: accent }}
            checked={confirmed}
            onChange={(e) => {
              setConfirmed(e.target.checked);
              setError(null);
            }}
          />
          <span className="text-base font-medium text-slate-900">{label}</span>
        </label>

        {error ? (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <button
        type="button"
        onClick={handleContinue}
        disabled={!confirmed || submitting}
        className="mt-5 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        style={{ backgroundColor: accent, color: ink }}
      >
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
        {submitting ? 'Confirming…' : 'Confirm & continue'}
      </button>
    </section>
  );
}
